import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AlertService } from 'app/services/alert.service';
import { catchError, map, of, switchMap } from 'rxjs';
import { AdapterService } from '../services/adapter.service';
import { SquareRefundsService } from '../services/square-refunds.service';
import * as SquareRefundsActions from './square-refunds.actions';

@Injectable()
export class SquareRefundsEffects {
    private _actions$ = inject(Actions);
    private _squareRefundsService = inject(SquareRefundsService);
    private _alertService = inject(AlertService);
    private adapter = inject(AdapterService);

    messages = {
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    getAllSquareRefunds$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareRefundsActions.loadSquareRefunds),
            switchMap((action) =>
                this._squareRefundsService.getAll(action.params).pipe(
                    map((res: any) => {
                        console.log(res);
                        const data = this.adapter.adaptAll(res.data ?? []);
                        const totalCount = res.pagination?.total ?? 0;
                        const totals = res.totals
                            ? this.adapter.adaptTotals(res.totals)
                            : null;
                        return SquareRefundsActions.loadSquareRefundsSuccess({
                            squareRefunds: data,
                            totalCount,
                            totals,
                        });
                    }),
                    catchError((error) =>
                        of(
                            SquareRefundsActions.loadSquareRefundsFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    getUnlinkedRefunds$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareRefundsActions.loadSquareUnlinkedRefunds),
            switchMap((action) =>
                this._squareRefundsService.getUnlinked(action.params).pipe(
                    map((res: any) => {
                        const data = this.adapter.adaptAll(res.data ?? []);
                        // Unlinked endpoint returns no pagination — use data.length
                        const totalCount = data.length;
                        const totals = res.totals
                            ? this.adapter.adaptTotals(res.totals)
                            : null;
                        return SquareRefundsActions.loadSquareUnlinkedRefundsSuccess(
                            {
                                unlinkedRefunds: data,
                                totalCount,
                                totals,
                            }
                        );
                    }),
                    catchError((error) =>
                        of(
                            SquareRefundsActions.loadSquareUnlinkedRefundsFailure(
                                {
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                }
                            )
                        )
                    )
                )
            )
        )
    );

    markExpensedRefunds$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareRefundsActions.markExpensedRefunds),
            switchMap((action) =>
                this._squareRefundsService.markExpensed(action.params).pipe(
                    map((res: any) => {
                        // console.log(res);
                        return SquareRefundsActions.markExpensedRefundsSuccess({
                            refundId: action.params.refundId,
                            message: res.message,
                        });
                    }),
                    catchError((error) =>
                        of(
                            SquareRefundsActions.markExpensedRefundsFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );
}
